import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { Book, BookPageType, BookStatus } from '../data/book';
import { environment } from './../../environments/environment';
import { ConfigService } from './config.service';
import { ImagesCacheService } from './images-cache.service';
import { ReportService } from './report.service';
import * as dicomParser from 'dicom-parser';
import { PresentationStateService, addToProvider } from './dicom/presentation-state.service';
import { findFirstElement } from './dicom/TagsUtils';
import { SafeHtml } from '@angular/platform-browser';
import { TemporaryToken } from '../data/temporaryToken';
import { TokenPermissions } from '../data/permissions';

export interface Download {
  state: 'PENDING' | 'IN_PROGRESS' | 'DONE'
  progress: number
  content: Blob | null
}

@Injectable({
  providedIn: 'root'
})
export class BookService {

  constructor(
    private http: HttpClient,
    private imagesService: ImagesCacheService,
    private configService: ConfigService,
    private reportService: ReportService,
    private presentationStateService: PresentationStateService
  ) { }

  currentBook: Book | null = null;
  currentBookWithReports: any | null = null;

  temporaryToken: TemporaryToken | null = null;

  url: string = "";

  loadBookByToken(token: string): Promise<void> {
    //console.log('token', token);
    
    return new Promise(async (resolve, reject) => {
      if (!environment.demo) {
        this.url = await this.configService.BOOK_URL;
        this.url += '/bookAccess/getJSONByToken?token=' + token;
        this.http.get<string>(this.url, { withCredentials: true, responseType: 'string' as 'json' }).subscribe(json => {
          const book = JSON.parse(json) as Book;
          console.log('book', book);
          this.currentBook = Book.sanitize(book);
          this.currentBookWithReports = Book.sanitize(book);
          resolve();
        });
      } else {
        let url = '';
        console.log('token', token);
        switch (token) {
          case 'demoMG_ST':
            url = 'assets/books/demoMG_ST/book.json';
            break;
          case 'demoCT_ST':
            url = 'assets/books/demoCT_ST/book.json';
            break;
          case 'demoMR_ST':
            url = 'assets/books/demoMR_ST/book.json';
            break;
          case 'demoPET_ST':
            url = 'assets/books/demoPET_ST/book.json';
            break;
          case 'demoCHECKUP':
            url = 'assets/books/demoCHECKUP/book.json';
            break;
          case 'demoMR_STPLUS':
            url = 'assets/books/demoMR_STPLUS/book.json';
            break;
          case 'demoCT_STPLUS':
            url = 'assets/books/demoCT_STPLUS/book.json';
            break;
          case 'demoMG_STPLUS':
            url = 'assets/books/demoMG_STPLUS/book.json';
            break;
          case 'demoCR_STPLUS':
            url = 'assets/books/demoCR_STPLUS/book.json';
            break;
          case 'demoCT_D':
            url = 'assets/books/demoCT_D/book.json';
            break;
          case 'demoMR_D':
            url = 'assets/books/demoMR_D/book.json';
            break;
          default:
            url = `assets/books/${token}/book.json`;
            console.log('url', url);
            break;
        }
        this.http.get<string>(url, { responseType: 'string' as 'json' }).subscribe(json => {
          const book = JSON.parse(json) as Book;
          console.log('book', book);
          this.currentBook = Book.sanitize(book);
          this.currentBookWithReports = Book.sanitize(book);
          if (this.currentBook?.activatedAI) {
            // Por defecto desactivado
            // this.reportService.activatedAI = true;
            this.reportService.loadAIData();
          }
          resolve();
        });
      }
    });
  }

  startLoadingBook() {
    this.startLoadingBookImages();
    this.loadPRs();
  }

  startLoadingBookImages() {
    //Primero los informes
    this.reportService.loadReports(this.currentBook?.reports).then(() => {
      const reportsToAdd: any[] = []
      const reportToPage = (r: { data: SafeHtml | string, auxData: SafeHtml[] | string[], type: 'pdf' | 'png' | 'html', studyUID: string }): any[] => {
        return r.auxData.map(d => {
          return {
            type: BookPageType.REPORT,
            report: d
          }
        })
      }
      this.reportService.reports.forEach(r => {
        console.log('reportToPage(r)', reportToPage(r));

        reportsToAdd.push(...reportToPage(r))
      })
      console.log('reportsToAdd', reportsToAdd);
      this.currentBookWithReports.pages.splice(1, 0, ...reportsToAdd);
    })
    //Primero la primera de cada
    this.currentBook!.pages.forEach(p => {
      p.bookImages.forEach(img => {
        if (img.pngFile != null) {
          //TODO compatibilizar esto con un array de studyuids
          this.imagesService.addImage(this.currentBook!.studyUIDs[0], img.pngFile[0]);
        }
        if (img.mp4File != null) {
          this.imagesService.addImage(this.currentBook!.studyUIDs[0], img.mp4File);
        }
      })
    })
    //Después el resto. No me preocupo por duplicados porque imageService.addImage lo controla
    this.currentBook!.pages.forEach(p => {
      p.bookImages.forEach(img => {
        if (img.pngFile != null && img.mp4File == null) {
          img.pngFile.forEach(png => {
            //TODO compatibilizar esto con un array de studyuids
            this.imagesService.addImage(this.currentBook!.studyUIDs[0], png);
          })
        }
      })
    });
  }

  loadPRs() {
    return new Promise<void>((resolve, reject) => {
      if (!environment.demo) {
        this.configService.BOOK_URL.then(BOOK_URL => {
          const url = `${BOOK_URL}/bookAccess/getPRFiles?studyUID=${this.currentBook!.studyUIDs.join(',')}`;
          this.http.get<string[]>(url, { responseType: 'json' }).subscribe(prs => {
            prs.forEach(pr => {
              this.imagesService.getFile(this.currentBook!.studyUIDs.join(','), pr).then((blob => {
                blob.arrayBuffer().then(arrayBuffer => {
                  const dataSet = dicomParser.parseDicom(new Uint8Array(arrayBuffer));
                  addToProvider(dataSet);
                })
              }))

            })
            resolve();
          });
        });
      } else {
        resolve();
      }
    })
  }

  checkBookStatus(token: string): Observable<BookStatus> {
    const subject = new Subject<BookStatus>();
    if (!environment.demo) {
      this.configService.BOOK_URL.then(BOOK_URL => {
        const url = BOOK_URL + '/bookAccess/checkBookStatusByToken?token=' + token;
        this.http.get<BookStatus>(url, { withCredentials: true, responseType: 'string' as 'json' }).subscribe(res => {
          subject.next(res);
          subject.complete();
        }, error => {
          subject.error(error);
        })
      });
    } else {
      setTimeout(() => {
        switch (token) {
          case 'demoExpired':
            subject.next(BookStatus.EXPIRED);
            break;
          default:
            subject.next(BookStatus.READY);
            break;
        }
        subject.complete();
      }, 25);
    }
    return subject.asObservable();
  }

  checkBookStatusByExternalToken(token: string): Observable<BookStatus> {
    const subject = new Subject<BookStatus>();
    this.configService.BOOK_URL.then(BOOK_URL => {
      const url = BOOK_URL + '/bookAccess/checkBookStatusByExternalToken?token=' + token;
      this.http.get<BookStatus>(url, { withCredentials: true, responseType: 'string' as 'json' }).subscribe(res => {
        subject.next(res);
        subject.complete();
      }, error => {
        subject.error(error);
      })
    });
    return subject.asObservable();
  }


  checkExpiringTime(token: string): Observable<number> {
    const subject = new Subject<number>();
    if (!environment.demo) {
      this.configService.BOOK_URL.then(BOOK_URL => {
        const url = BOOK_URL + '/bookAccess/checkExpiringTime?token=' + token;
        this.http.get<number>(url, { withCredentials: true, responseType: 'number' as 'json' }).subscribe(res => {
          subject.next(+res);
          subject.complete();
        }, error => {
          subject.error(error);
        })
      });
    } else {
      setTimeout(() => {
        subject.next(new Date().getTime() + 432000000);
        subject.complete();
      }, 25);
    }
    return subject.asObservable();
  }

  renewExpirationDate(token: string): Observable<number> {
    const subject = new Subject<number>();
    if (!environment.demo) {
      this.configService.BOOK_URL.then(BOOK_URL => {
        const url = BOOK_URL + '/bookAccess/renewExpirationDate?token=' + token;
        this.http.get<number>(url, { withCredentials: true, responseType: 'number' as 'json' }).subscribe(res => {
          subject.next(+res);
          subject.complete();
        }, error => {
          subject.error(error);
        })
      });
    } else {
      setTimeout(() => {
        subject.next(new Date().getTime() + 3024000000);
        subject.complete();
      }, 25);
    }
    return subject.asObservable();
  }

  getSurroundingImages(route: string, numberOfImages: number): string[] {
    route = route.split('\\')[route.split('\\').length - 1]
    console.log('Buscando la imagen:', route);
    const serie = this.currentBook!.studyInfo.find(s => s.files.some(f => f == route) || s.files.some(f => f.split('\\')[f.split('\\').length - 1] == route));
    console.log('La serie es:', serie);
    const imageIndex = serie!.files.findIndex(img => img == route);
    console.log('El index de la imagen es:', imageIndex);
    let images: any[] = [];
    for (let i = imageIndex - numberOfImages; i <= imageIndex + numberOfImages; i++)
      if (i >= 0 && i < serie!.files.length && i != imageIndex) {
        images.push({ img: serie!.files[i], i });
      }

    console.log('Images to ask for 1: ', images.map(img => img.i));
    let distance = (a: any, t: number) => Math.abs(t - a.i);
    images = images.sort((a, b) => distance(a, imageIndex) - distance(b, imageIndex)).map(img => img.img);

    console.log('Images to ask for: ', images);
    return images;
  }

  getMoreImages(where: 'before' | 'after', route: string): string | null {
    route = route.split('\\')[route.split('\\').length - 1]
    const serie = this.currentBook!.studyInfo.find(s => s.files.some(f => f == route) || s.files.some(f => f.split('\\')[f.split('\\').length - 1] == route));
    let imageIndex = serie!.files.findIndex(img => img == route);
    if (where == 'before') {
      while (imageIndex > 0 && this.imagesService.isLoaded(serie!.files[--imageIndex])) {
        ;
      }
      if (imageIndex >= 0) {
        return serie!.files[imageIndex];
      } else {
        return null;
      }
    }
    if (where == 'after') {
      while (imageIndex < serie!.files.length - 1 && this.imagesService.isLoaded(serie!.files[++imageIndex])) {
        ;
      }
      if (imageIndex < serie!.files.length) {
        return serie!.files[imageIndex];
      } else {
        return null;
      }
    }
    return null;
  }

  sendNotifToMail(mail: string, token: string): Observable<void> {
    const url = `${this.configService.syncBOOK_URL}/bookAccess/sendMailForBook?mail=${mail}&token=${token}`
    if (!environment.demo) {
      return this.http.post<void>(url, { mail, token }, { withCredentials: true, responseType: 'boolean' as 'json' });
    } else {
      const subject = new Subject<void>();
      setTimeout(() => {
        subject.next();
        subject.complete();
      }, 25);
      return subject.asObservable();
    }
  }

  sendBookMailByPRP(mail: string, token: string): Observable<boolean> {
    const url = `${this.configService.syncBOOK_URL}/bookAccess/sendBookMailByPRP?mail=${mail}&token=${token}`
    if (!environment.demo) {
      return this.http.post<boolean>(url, { withCredentials: true, responseType: 'boolean' as 'json' });
    } else {
      const subject = new Subject<boolean>();
      setTimeout(() => {
        subject.next(true);
        subject.complete();
      }, 25);
      return subject.asObservable();
    }
  }

  waitForBook(): Promise<Book> {
    return new Promise<Book>((resolve, reject) => {
      if (this.currentBook != null) {
        resolve(this.currentBook)
      } else {
        setTimeout(() => {
          return this.waitForBook();
        }, 500);
      }
    })
  }

  askForBook(tkn: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!environment.demo) {
        this.configService.BOOK_URL.then(BOOK_URL => {
          const url = BOOK_URL + '/bookAccess/askForBook?tkn=' + tkn;
          this.http.get<void>(url, { withCredentials: true, responseType: 'json' }).subscribe(res => {
            resolve();
          }, error => {
            reject(error);
          })
        });
      } else {
        resolve();
      }
    })
  }

  getLocatorByToken(token: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (!environment.demo) {
        if (this.temporaryToken?.locator != null) {
          resolve(this.temporaryToken.locator);
        } else {
          this.configService.BOOK_URL.then(BOOK_URL => {
            const url = BOOK_URL + '/bookAccess/getLocatorByToken?token=' + token;
            this.http.get<string>(url, { withCredentials: true, responseType: 'string' as 'json' }).subscribe(res => {
              resolve(res);
            }, error => {
              reject(error);
            })
          });
        }
      } else {
        resolve('123AAA')
      }
    })
  }

  downloadInstructions(bookId: number) {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      this.http.get(`${this.url}/getInstructionsById?bookId=${bookId}`, { withCredentials: true, responseType: 'arraybuffer' }).subscribe({
        next: res => {
          resolve(res);
        },
        error: err => {
          console.error(err);
          reject(err);
        }
      });

    })
  }

  /**
   * Solicita un token temporal para acceder al book desde el visor DICOM
   * @param tkn 
   * @returns El token temporal
   */
  askForToken(tkn: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (!environment.demo) {
        this.configService.BOOK_URL.then(BOOK_URL => {
          const url = BOOK_URL + '/bookAccess/askForVisor?tkn=' + tkn;
          this.http.get(url, { withCredentials: true, responseType: 'text'}).subscribe(res => {
            resolve(res);
          }, error => {
            reject(error);
          })
        });
      } else {
        resolve('123AAA');
      }
    });
  }

  // Obtiene el patientId asociado a un token de book
  getPatientIdByToken(token: string): Promise<string | null> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(BOOK_URL => {
        const url = `${BOOK_URL}/bookAccess/getPatientIdByToken?token=${token}`;
        this.http.get<string>(url, { withCredentials: true }).subscribe(
          patientId => resolve(patientId),
          error => {
            console.error('Error obteniendo patientId:', error);
            resolve(null);
          }
        );
      });
    });
  }

  
  //Obtiene los permisos por defecto configurados en el backend
  getDefaultPermissions(): Promise<TokenPermissions[]> {
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(BOOK_URL => {
        const url = `${BOOK_URL}/bookAccess/getDefaultPermissions`;
        this.http.get<TokenPermissions[]>(url, { withCredentials: true }).subscribe(
          permissions => resolve(permissions),
          error => {
            console.error('Error obteniendo permisos por defecto:', error);
            reject(error);
          }
        );
      });
    });
  }

  addBookIDToSession(authTkn: string|null, studyToken: string|null): Promise<void> {
    if(studyToken==null || authTkn==null) {
      return Promise.reject('El authTkn o studyToken no pueden ser null');
    }
    return new Promise((resolve, reject) => {
      this.configService.BOOK_URL.then(BOOK_URL => {
        const url = `${BOOK_URL}/bookAccess/addBookIDToSession?authTkn=${authTkn}`;
        this.http.post<void>(url, studyToken, { withCredentials: true }).subscribe(
          () => resolve(),
          error => {
            console.error('Error añadiendo el ID del book a la sesión:', error);
            reject(error);
          }
        );
      });
    });
  }

}
